import { useState } from "react";
import type { Column, Table } from "@tanstack/react-table";
import { ChevronDown, ChevronUp, Search } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

import { cn } from "@/lib/utils";

import type { FilterOption, GroupedFilterOptions } from "./types";

interface DataTableFilterOptionsProps<TData, TValue> {
  column: Column<TData, TValue> | null;
  table: Table<TData>;
}

export function DataTableFilterOptions<TData, TValue>({
  column,
  table,
}: DataTableFilterOptionsProps<TData, TValue>) {
  const [searchValue, setSearchValue] = useState("");
  const [expandedGroups, setExpandedGroups] =
    useState<Set<string>>(new Set());

  const filterValue =
    column?.getFilterValue() as string[] | undefined | null;

  // Compute filter options directly on each render.
  // All current getColumnFilters implementations are synchronous,
  // so this always returns fresh data when tenants/statistics load.
  const filterResult =
    column?.columnDef.meta?.getColumnFilters?.({ column, table });
  const rawOptions =
    filterResult instanceof Promise ? [] : (filterResult ?? []);

  const selectedValues = new Set(filterValue ?? []);

  // Filter options based on search
  const filteredGroupedOptions: GroupedFilterOptions[] = [];
  const filteredFlatOptions: FilterOption[] = [];

  if (Array.isArray(rawOptions)) {
    rawOptions.forEach((item) => {
      if ("groupLabel" in item && "options" in item) {
        const group = item;
        const matchingOptions = group.options.filter(
          (option) =>
            option.label
              .toLowerCase()
              .includes(searchValue.toLowerCase()),
        );
        if (matchingOptions.length > 0) {
          filteredGroupedOptions.push({
            groupLabel: group.groupLabel,
            options: matchingOptions,
          });
        }
      } else if ("label" in item && "value" in item) {
        const option = item;
        if (
          option.label
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        ) {
          filteredFlatOptions.push(option);
        }
      }
    });
  }

  const hasGroupedOptions = filteredGroupedOptions.length > 0;
  const hasFlatOptions = filteredFlatOptions.length > 0;
  const hasAnyOptions = hasGroupedOptions || hasFlatOptions;

  if (!column) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Select a filter category
      </div>
    );
  }

  const handleToggleOption = (optionValue: string) => {
    const currentValue =
      column.getFilterValue() as string[] | undefined | null;
    const newSelectedValues = new Set(currentValue ?? []);

    if (newSelectedValues.has(optionValue)) {
      newSelectedValues.delete(optionValue);
    } else {
      newSelectedValues.add(optionValue);
    }

    const filterValues = Array.from(newSelectedValues);
    column.setFilterValue(
      filterValues.length ? filterValues : undefined,
    );
  };

  const handleToggleGroup = (group: GroupedFilterOptions) => {
    const groupValues = group.options.map((opt) => opt.value);
    const newSelectedValues = new Set(selectedValues);

    const allSelected = groupValues.every((value) =>
      selectedValues.has(value),
    );

    if (allSelected) {
      groupValues.forEach((value) =>
        newSelectedValues.delete(value),
      );
    } else {
      groupValues.forEach((value) => newSelectedValues.add(value));
    }

    const filterValues = Array.from(newSelectedValues);
    column.setFilterValue(
      filterValues.length ? filterValues : undefined,
    );
  };

  const toggleGroupExpansion = (groupLabel: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (expandedGroups.has(groupLabel)) {
      newExpandedGroups.delete(groupLabel);
    } else {
      newExpandedGroups.add(groupLabel);
    }
    setExpandedGroups(newExpandedGroups);
  };

  return (
    <div className="flex flex-[1_1_0] flex-col">
      {/* Search input */}
      <div className="p-2">
        <div className="relative">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search..."
            inputSize="sm"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            className="pl-9"
          />
        </div>
      </div>

      <Separator />

      {/* Options list */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2">
          {/* Render grouped options first */}
          {filteredGroupedOptions.map((group) => {
            const groupValues = group.options.map(
              (opt) => opt.value,
            );
            const allSelected = groupValues.every((value) =>
              selectedValues.has(value),
            );
            const someSelected =
              groupValues.some((value) =>
                selectedValues.has(value),
              ) && !allSelected;
            const isExpanded = expandedGroups.has(
              group.groupLabel,
            );

            return (
              <div key={group.groupLabel}>
                <div
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <Checkbox
                    checked={
                      allSelected
                        ? true
                        : someSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={() => {
                      handleToggleGroup(group);
                    }}
                  />
                  <span
                    className="flex-1"
                    onClick={() => {
                      toggleGroupExpansion(group.groupLabel);
                    }}
                  >
                    {group.groupLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      toggleGroupExpansion(group.groupLabel);
                    }}
                    className="flex items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {isExpanded ? (
                      <ChevronUp className="size-4" />
                    ) : (
                      <ChevronDown className="size-4" />
                    )}
                  </button>
                </div>

                {isExpanded &&
                  group.options.map((option) => {
                    const isSelected = selectedValues.has(
                      option.value,
                    );

                    return (
                      <div
                        key={option.value}
                        className={cn(
                          "ml-6 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
                          "hover:bg-accent hover:text-accent-foreground",
                        )}
                        onClick={() => {
                          handleToggleOption(option.value);
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => {
                            handleToggleOption(option.value);
                          }}
                          className="pointer-events-none"
                        />
                        {option.icon && (
                          <option.icon className="size-4 text-muted-foreground" />
                        )}
                        <span className="flex-1">
                          {option.label}
                        </span>
                        {option.uniqueValues && (
                          <span className="text-xs text-muted-foreground">
                            {option.uniqueValues}
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {/* Render flat options after grouped options */}
          {filteredFlatOptions.map((option) => {
            const isSelected = selectedValues.has(option.value);

            return (
              <div
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                )}
                onClick={() => {
                  handleToggleOption(option.value);
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => {
                    handleToggleOption(option.value);
                  }}
                  className="pointer-events-none"
                />
                {option.icon && (
                  <option.icon className="size-4 text-muted-foreground" />
                )}
                <span className="flex-[1_1_0]">
                  {option.label}
                </span>
                {option.uniqueValues && (
                  <span className="text-xs text-muted-foreground">
                    {option.uniqueValues}
                  </span>
                )}
              </div>
            );
          })}

          {!hasAnyOptions && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No options found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
